import { useForm, Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { CreditCard, CheckCircle2, Clock, Shield } from 'lucide-react'

interface Payment {
  id: string
  service_fee_cents: number
  gov_fee_cents: number
  total_cents: number
  currency: string
  paid: boolean
  paid_at: string | null
  stripe_checkout_session_id: string | null
  invoice?: {
    number: string
    issued_at: string
  }
  receipt?: {
    number: string
    issued_at: string
    payment_method: string
  }
}

type Props = {
  registered_address: { id: string; type_name: string; duration_months: number }
  payment: Payment | null
  type: { gov_fee_cents: number; service_fee_cents: number; total_cents: number; min_duration_months: number }
  stripe_publishable_key: string
}

export default function PaymentShow({ registered_address, payment, type }: Props) {
  const basePath = useBasePath()
  const form = useForm({})
  const paymentCancelled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'cancel'

  const serviceFee = Math.round(type.service_fee_cents * registered_address.duration_months / 12)
  const govFee = Math.round(type.gov_fee_cents * registered_address.duration_months / 12)
  const totalCost = serviceFee + govFee

  return (
    <DashboardLayout
      title={`Payment — ${registered_address.type_name}`}
      breadcrumbs={[
        { label: 'Registered Addresses', href: 'registered_addresses' },
        { label: registered_address.type_name, href: `registered_addresses/${registered_address.id}` },
        { label: 'Payment' }
      ]}
    >
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Payment</h1>
            {payment?.paid && (
              <Badge className="font-normal text-xs bg-green-600 text-white">Paid</Badge>
            )}
            {payment && !payment.paid && (
              <Badge className="font-normal text-xs bg-secondary text-secondary-foreground">Pending</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{registered_address.type_name}</p>
        </div>

        {paymentCancelled && (
          <div className="border border-dashed rounded-lg p-4 mb-6 bg-muted/30">
            <p className="text-sm font-medium text-foreground">Payment cancelled</p>
            <p className="text-xs text-muted-foreground mt-1">You can try again when you're ready.</p>
          </div>
        )}

        {!payment ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Summary</h2>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee ({registered_address.duration_months} months)</span>
                  <span className="text-foreground">{formatCurrency(serviceFee)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(govFee)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(totalCost)}</span>
                </div>
              </div>
            </section>

            <div className="border rounded-lg px-4 py-3">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">7-day money-back guarantee</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Full refund if we haven't started your service</p>
                </div>
              </div>
            </div>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/registered_addresses/${registered_address.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Processing...' : `Pay ${formatCurrency(totalCost)}`}
              </Button>
            </form>
          </div>
        ) : payment.paid ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Payment Complete</h2>
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Paid on {new Date(payment.paid_at!).toLocaleDateString()}
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total Paid</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
                {payment.receipt && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Receipt</span>
                    <span className="font-mono">{payment.receipt.number}</span>
                  </div>
                )}
                {payment.invoice && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                    <span>Invoice</span>
                    <span className="font-mono">{payment.invoice.number}</span>
                  </div>
                )}
              </div>
            </section>

            <Button asChild variant="outline" className="w-full">
              <Link href={`${basePath}/registered_addresses/${registered_address.id}`}>Back to Service</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Payment Pending</h2>
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Clock className="w-3 h-3" />
                  Complete checkout to proceed
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
              </div>
            </section>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/registered_addresses/${registered_address.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Redirecting...' : `Pay ${formatCurrency(payment.total_cents)}`}
              </Button>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
